import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const ADMIN_TOKEN_KEY = 'admin_reports_token';
const ADMIN_USER_KEY = 'admin_reports_user';

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value || 0));
const formatDateTimeIST = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export default function AdminTutors() {
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tutorRegistry, setTutorRegistry] = useState([]);

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_USER_KEY);
    setAdminToken('');
    setTutorRegistry([]);
  };

  const loadTutorRegistry = async (token = adminToken) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin/reports/funnel?days=30', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to load tutor details');
      }
      setTutorRegistry(Array.isArray(data?.tutor_registry) ? data.tutor_registry : []);
    } catch (err) {
      const message = err.message || 'Failed to load tutor details';
      setError(message);
      if (message.toLowerCase().includes('session') || message.toLowerCase().includes('unauthorized')) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      loadTutorRegistry(adminToken);
    }
  }, [adminToken]);

  if (!adminToken) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <div>
              <div className="text-xl font-bold text-slate-900">Tricity Tutors</div>
              <div className="text-xs text-slate-500">Tutor Details</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/admin">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back To Admin
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/leads">Lead Details</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/students">Student Details</Link>
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Tutors Details</CardTitle>
              <CardDescription>All tutors with registration date/time in IST and unlocked leads.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline">{formatNumber(tutorRegistry.length)} tutors</Badge>
              <Button onClick={() => loadTutorRegistry()} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading' : 'Refresh'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tutor Name</TableHead>
                    <TableHead>Registration Date/Time (IST)</TableHead>
                    <TableHead>Login Count</TableHead>
                    <TableHead>Leads Unlocked</TableHead>
                    <TableHead>Last Requirement Seen (IST)</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tutorRegistry.map((row) => (
                    <TableRow key={`${row.email}-${row.created_at}`}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{formatDateTimeIST(row.created_at)}</TableCell>
                      <TableCell>{formatNumber(row.login_count)}</TableCell>
                      <TableCell>{formatNumber(row.lead_unlocks)}</TableCell>
                      <TableCell>{formatDateTimeIST(row.last_requirement_seen_at)}</TableCell>
                      <TableCell>{row.mobile || '-'}</TableCell>
                      <TableCell>{row.email || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
